import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Building2, ShieldAlert, MessageSquare, Briefcase, CheckCircle2 } from 'lucide-react';
import { LegalShell } from '@/components/public/LegalShell';

interface ContactCard {
  Icon: React.ComponentType<{ className?: string }>;
  id: string;
  email: string;
  titleKey: string;
  bodyKey: string;
}

const CARDS: ContactCard[] = [
  { Icon: MessageSquare, id: 'support',  email: 'support@keenez.com',  titleKey: 'contact.cards.support.title',  bodyKey: 'contact.cards.support.body' },
  { Icon: Briefcase,     id: 'sales',    email: 'sales@keenez.com',    titleKey: 'contact.cards.sales.title',    bodyKey: 'contact.cards.sales.body' },
  { Icon: ShieldAlert,   id: 'security', email: 'security@keenez.com', titleKey: 'contact.cards.security.title', bodyKey: 'contact.cards.security.body' },
  { Icon: Building2,     id: 'press',    email: 'press@keenez.com',    titleKey: 'contact.cards.press.title',    bodyKey: 'contact.cards.press.body' },
];

export function ContactPage() {
  const { t } = useTranslation();
  return (
    <LegalShell
      eyebrow={t('contact.eyebrow')}
      title={t('contact.title')}
      subtitle={t('contact.subtitle')}
    >
      <div className="grid sm:grid-cols-2 gap-4 mt-2">
        {CARDS.map((c, i) => (
          <motion.a
            key={c.id}
            id={c.id}
            href={`mailto:${c.email}`}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            whileHover={{ y: -3 }}
            className="block rounded-2xl border border-border bg-card p-5 shadow-soft hover:shadow-card-lift transition scroll-mt-24"
          >
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
              <c.Icon className="h-4 w-4" />
            </span>
            <h3 className="mt-3 font-semibold">{t(c.titleKey)}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t(c.bodyKey)}</p>
            <span className="num mt-2 inline-block text-sm font-medium text-primary">{c.email}</span>
          </motion.a>
        ))}
      </div>

      <ContactForm />

      <section className="mt-10 rounded-2xl border border-border bg-muted/30 p-6">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t('contact.offices.title')}</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
          <div>
            <p className="font-semibold">Tokyo</p>
            <p className="text-muted-foreground">{t('contact.offices.tokyo')}</p>
          </div>
          <div>
            <p className="font-semibold">Berlin</p>
            <p className="text-muted-foreground">{t('contact.offices.berlin')}</p>
          </div>
        </div>
      </section>
    </LegalShell>
  );
}

function ContactForm() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) return;
    // No backend endpoint yet — open the user's mail client with the
    // message pre-filled. Real submission will be wired post-launch.
    const subject = encodeURIComponent(`KeenEZ — ${name}`);
    const body = encodeURIComponent(`${message}\n\n— ${name} <${email}>`);
    window.location.href = `mailto:hello@keenez.com?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <section className="mt-12 rounded-3xl border border-border bg-card shadow-soft p-6 md:p-8">
      <div className="flex items-center gap-3 mb-4">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
          <Mail className="h-4 w-4" />
        </span>
        <div>
          <h2 className="text-lg font-semibold">{t('contact.form.title')}</h2>
          <p className="text-xs text-muted-foreground">{t('contact.form.subtitle')}</p>
        </div>
      </div>

      {sent ? (
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-500/10 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-700 dark:text-emerald-300">{t('contact.form.sent')}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <Field label={t('contact.form.name')} value={name} onChange={setName} required />
          <Field label={t('contact.form.email')} value={email} onChange={setEmail} type="email" required />
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t('contact.form.message')}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              required
              className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
            />
          </div>
          <button
            type="submit"
            className="gradient-primary inline-flex h-11 items-center gap-2 px-6 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink transition"
          >
            {t('contact.form.submit')}
          </button>
        </form>
      )}
    </section>
  );
}

function Field({ label, value, onChange, type = 'text', required }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-11 rounded-xl border border-border bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
      />
    </div>
  );
}
